// ============================================================
// Loot Generation System — v3.0 Equipment Rework
// ============================================================
import { RARITY_DROP_WEIGHTS, RARITY_STAT_MULT, RARITY_SELL_MULT, LOOT_DROP_GLOBAL_MULT, } from '../constants/balance.js';
import { ARMOR_SUBTYPE_ARCHETYPE } from '../types/game.js';
/** Droppable slots — 'reserved' never drops */
const SLOT_LIST = ['weapon1', 'weapon2', 'armor', 'relic1', 'relic2', 'relic3', 'relic4'];
/** Weights for slot selection: weapons and armor are equal; each relic slot is 3× less likely */
const SLOT_WEIGHTS = [3, 3, 3, 1, 1, 1, 1]; // weapon1, weapon2, armor, relic1-4
const RARITY_LIST = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'];
// Item name templates per slot
const ITEM_NAMES = {
    // 14 weapon types × 2 named variants — same darker-palette pattern as armor.
    // Each pair has a "balanced" baseline + a "leaning" variant that drops a stat
    // hard to boost another. Type + lean live in WEAPON_NAME_TYPE / WEAPON_NAME_LEAN.
    // weapon1: 12 types (everything except shield + focus, which are off-hand only)
    weapon1: [
        'Bonecleaver', 'Runewhisper', // sword
        "Reaver's Cleave", 'Bloodfang', // axe
        'Skullcrusher', 'Soulbreaker', // mace
        'Shadow Fang', 'Venom Kiss', // dagger
        'Hexrod', 'Doomwand', // wand
        'Hexblade Star', 'Soulshard', // shuriken
        "Reaper's Knife", 'Whisper Knife', // throwing_knife
        'Wraithbow', 'Spinereaper', // bow (2H)
        'Doomspear', 'Soul-piercer', // spear (2H)
        'Soulflame Staff', 'Doomscepter', // staff (2H)
        "Headsman's Blade", 'Worldsplitter', // sword_2h
        'Tombcrusher', 'Worldreaver', // mace_2h
    ],
    // weapon2: 9 types (no 2H weapons; shield + focus added)
    weapon2: [
        'Bonecleaver', 'Runewhisper',
        "Reaver's Cleave", 'Bloodfang',
        'Skullcrusher', 'Soulbreaker',
        'Shadow Fang', 'Venom Kiss',
        'Hexrod', 'Doomwand',
        'Hexblade Star', 'Soulshard',
        "Reaper's Knife", 'Whisper Knife',
        'Soulflame Staff', 'Doomscepter', // staff slotted in weapon2 too
        'Aegis of Bone', 'Bulwark of Dread', // shield
        'Spirit Wisp', 'Souldrain Focus', // focus
    ],
    // 16 darker-themed armor names — each maps to a sub-type via ARMOR_NAME_SUBTYPE
    // and a per-name stat lean via ARMOR_NAME_LEAN. Pool layout per sub-type:
    //   plate (3): Wraith Mail / Dread Plate / Ironwarden
    //   brigandine (2): Bonecage / Grim Hauberk
    //   skirmisher (3): Serpentskin / Spiked Hide / Bloodharness
    //   hunter (3): Stalker's Vest / Reaper's Coat / Shadewalker Garb
    //   robe (2): Ashen Robe / Whisper Silk
    //   vestment (3): Dreadweave Vestment / Heretic's Habit / Cursed Mystic
    armor: ['Wraith Mail', 'Dread Plate', 'Ironwarden',
        'Bonecage', 'Grim Hauberk',
        'Serpentskin', 'Spiked Hide', 'Bloodharness',
        "Stalker's Vest", "Reaper's Coat", 'Shadewalker Garb',
        'Ashen Robe', 'Whisper Silk',
        'Dreadweave Vestment', "Heretic's Habit", 'Cursed Mystic'],
    reserved: ['???'],
    relic1: ['Sigil', 'Orb', 'Idol', 'Talisman', 'Emblem', 'Charm', 'Totem'],
    relic2: ['Sigil', 'Orb', 'Idol', 'Talisman', 'Emblem', 'Charm', 'Totem'],
    relic3: ['Sigil', 'Orb', 'Idol', 'Talisman', 'Emblem', 'Charm', 'Totem'],
    relic4: ['Sigil', 'Orb', 'Idol', 'Talisman', 'Emblem', 'Charm', 'Totem'],
};
const RARITY_PREFIXES = {
    common: ['Worn', 'Simple', 'Basic'],
    uncommon: ['Sturdy', 'Fine', 'Polished'],
    rare: ['Enchanted', 'Gleaming', 'Runic'],
    epic: ['Arcane', 'Mythic', 'Radiant'],
    legendary: ['Godforged', 'Eternal', 'Abyssal'],
    unique: ['Unique'], // never used for name generation but satisfies the Record type
};
// ─────────────────────────────────────────────────────────────────────────────
// Armor identity tables — keep in sync with ITEM_NAMES.armor (16 entries).
// ─────────────────────────────────────────────────────────────────────────────
/** Exact-name → sub-type lookup. Drives the armor case in `generateEquipment`. */
const ARMOR_NAME_SUBTYPE = {
    // Plate (heavy bulwark)
    'Wraith Mail': 'plate',
    'Dread Plate': 'plate',
    'Ironwarden': 'plate',
    // Brigandine (heavy bruiser)
    'Bonecage': 'brigandine',
    'Grim Hauberk': 'brigandine',
    // Skirmisher (medium burst)
    'Serpentskin': 'skirmisher',
    'Spiked Hide': 'skirmisher',
    'Bloodharness': 'skirmisher',
    // Hunter (medium sustained)
    "Stalker's Vest": 'hunter',
    "Reaper's Coat": 'hunter',
    'Shadewalker Garb': 'hunter',
    // Robe (pure caster)
    'Ashen Robe': 'robe',
    'Whisper Silk': 'robe',
    // Vestment (battle-mage)
    'Dreadweave Vestment': 'vestment',
    "Heretic's Habit": 'vestment',
    'Cursed Mystic': 'vestment',
};
const ARMOR_NAME_LEAN = {
    // Plate
    'Wraith Mail': { hpMult: 0, defMult: 1.5 }, // mobility plate — drops HP, DEF anchor
    'Dread Plate': {}, // balanced
    'Ironwarden': { blockMult: 0, hpMult: 1.5, defMult: 1.3 }, // ironwall — drops Block, pure bulwark
    // Brigandine
    'Bonecage': {}, // balanced
    'Grim Hauberk': { atkMult: 0, hpMult: 1.5, defMult: 1.4 }, // pure bruise — drops ATK
    // Skirmisher
    'Serpentskin': { atkMult: 0, defMult: 1.5, hpMult: 1.4 }, // defensive skirmisher — drops ATK
    'Spiked Hide': {}, // balanced
    'Bloodharness': { defMult: 0, atkMult: 1.5, critMult: 1.5, critDmgMult: 1.6 }, // glass burst — drops DEF
    // Hunter
    "Stalker's Vest": { atkSpdMult: 0, hpMult: 1.5, defMult: 1.3 }, // tanky hunter — drops AtkSpd
    "Reaper's Coat": {}, // balanced
    'Shadewalker Garb': { defMult: 0, atkSpdMult: 1.5, mpMult: 1.5 }, // glass DPS — drops DEF
    // Robe
    'Ashen Robe': {}, // balanced
    'Whisper Silk': { defMult: 0, spMult: 1.5 }, // glass cannon — drops DEF
    // Vestment
    'Dreadweave Vestment': {}, // balanced
    "Heretic's Habit": { spMult: 0, hpMult: 1.5, defMult: 1.3 }, // scholar — drops SP, bulky caster
    'Cursed Mystic': { hpMult: 0, spMult: 1.5, critMult: 1.5, critDmgMult: 1.3 }, // risk — drops HP
};
/**
 * Maps weapon/armor base names to icon sprite keys per rarity:
 * [common, uncommon, rare, epic, legendary, unique]
 */
const WEAPON_ICON_MAP = {
    '2H Sword': ['icon_sword2h_r', 'icon_sword2h_r', 'icon_sword2h_r', 'icon_sword2h_e', 'icon_sword2h_l', 'icon_sword2h_l'],
    '2H Mace': ['icon_mace2h_r', 'icon_mace2h_r', 'icon_mace2h_r', 'icon_mace2h_e', 'icon_mace2h_l', 'icon_mace2h_l'],
    Sword: ['icon_sword_c', 'icon_sword_u', 'icon_sword_r', 'icon_sword_e', 'icon_sword_l', 'icon_sword_q'],
    Axe: ['icon_axe_c', 'icon_axe_u', 'icon_axe_r', 'icon_axe_e', 'icon_axe_l', 'icon_axe_q'],
    Staff: ['icon_staff_c', 'icon_staff_u', 'icon_staff_r', 'icon_staff_e', 'icon_staff_l', 'icon_staff_q'],
    Bow: ['icon_bow_c', 'icon_bow_u', 'icon_bow_r', 'icon_bow_e', 'icon_bow_l', 'icon_bow_q'],
    Dagger: ['icon_dagger_c', 'icon_dagger_u', 'icon_dagger_r', 'icon_dagger_e', 'icon_dagger_l', 'icon_dagger_q'],
    Mace: ['icon_mace_c', 'icon_mace_u', 'icon_mace_r', 'icon_mace_e', 'icon_mace_l', 'icon_mace_q'],
    Shield: ['icon_shield_c', 'icon_shield_u', 'icon_shield_r', 'icon_shield_e', 'icon_shield_l', 'icon_shield_q'],
    Spear: ['icon_spear_c', 'icon_spear_u', 'icon_spear_r', 'icon_spear_e', 'icon_spear_l', 'icon_spear_l'],
    Shuriken: ['icon_shuriken', 'icon_shuriken', 'icon_shuriken_alt', 'icon_shuriken_alt', 'icon_shuriken_unique', 'icon_shuriken_unique'],
    'Throwing Knife': ['icon_knife_c', 'icon_knife_u', 'icon_knife_r', 'icon_knife_e', 'icon_knife_l', 'icon_knife_q'],
    Focus: ['icon_focus', 'icon_focus', 'icon_focus_alt', 'icon_focus_alt', 'icon_focus_alt', 'icon_focus_alt'],
    Wand: ['icon_wand_c', 'icon_wand_u', 'icon_wand_r', 'icon_wand_e', 'icon_wand_l', 'icon_wand_l'],
};
/** Legacy: maps base weapon name to WeaponType. Kept for back-compat with
 *  saved items rolled before the flavor-name pass — their `name` may still
 *  end with one of these keys. New rolls use WEAPON_NAME_TYPE for direct
 *  exact-match lookup and skip the suffix scan entirely. */
const NAME_TO_WEAPON_TYPE = {
    Sword: 'sword', Axe: 'axe', Staff: 'staff', Bow: 'bow',
    Dagger: 'dagger', Mace: 'mace', Spear: 'spear', Shuriken: 'shuriken',
    'Throwing Knife': 'throwing_knife', Shield: 'shield', Focus: 'focus', Wand: 'wand',
    '2H Sword': 'sword_2h', '2H Mace': 'mace_2h',
};
/** New: exact-match lookup from flavor name → weapon type. Mirrors
 *  ARMOR_NAME_SUBTYPE. Drives the rolled stat profile + class restrictions
 *  + special-effect pool selection. */
const WEAPON_NAME_TYPE = {
    // sword (1H)
    'Bonecleaver': 'sword',
    'Runewhisper': 'sword',
    // axe
    "Reaver's Cleave": 'axe',
    'Bloodfang': 'axe',
    // mace
    'Skullcrusher': 'mace',
    'Soulbreaker': 'mace',
    // dagger
    'Shadow Fang': 'dagger',
    'Venom Kiss': 'dagger',
    // wand
    'Hexrod': 'wand',
    'Doomwand': 'wand',
    // shuriken
    'Hexblade Star': 'shuriken',
    'Soulshard': 'shuriken',
    // throwing knife
    "Reaper's Knife": 'throwing_knife',
    'Whisper Knife': 'throwing_knife',
    // bow (2H)
    'Wraithbow': 'bow',
    'Spinereaper': 'bow',
    // spear (2H)
    'Doomspear': 'spear',
    'Soul-piercer': 'spear',
    // staff (2H)
    'Soulflame Staff': 'staff',
    'Doomscepter': 'staff',
    // 2H sword
    "Headsman's Blade": 'sword_2h',
    'Worldsplitter': 'sword_2h',
    // 2H mace
    'Tombcrusher': 'mace_2h',
    'Worldreaver': 'mace_2h',
    // shield (off-hand)
    'Aegis of Bone': 'shield',
    'Bulwark of Dread': 'shield',
    // focus (off-hand)
    'Spirit Wisp': 'focus',
    'Souldrain Focus': 'focus',
};
const WEAPON_NAME_LEAN = {
    // DESIGN RULE: lean variants trade ~30-40 % of their primary stat for a
    // 3-4× boost on the signature stat. Same expected damage at typical crit
    // stacking, different profile (reliable flat vs burst crit). The earlier
    // 50-60 % ATK cut paired with a merely ×2 critDmg bump left crit-lean
    // weapons feeling objectively weaker — flagged in playtest as "Bloodfang
    // is 77 ATK for +50 % critdmg vs Reaver's Cleave 252 ATK for +30 %".
    // sword — Runewhisper drops crit, lifts SP hard + a critDmg kicker
    'Bonecleaver': {},
    'Runewhisper': { critMult: 0.4, spMult: 2.0, critDmgMult: 1.5, forceRuneblade: true },
    // axe — Bloodfang reduces ATK 50 %, quadruples CritDmg (was 2.5×)
    "Reaver's Cleave": {},
    'Bloodfang': { atkMult: 0.5, critDmgMult: 4.0 },
    // mace — Soulbreaker: softer ATK cut, bigger HP / DEF boost (tank mace)
    'Skullcrusher': {},
    'Soulbreaker': { atkMult: 0.6, hpMult: 1.8, defMult: 1.7 },
    // dagger — Venom Kiss: reliable DPS side, ATK bumped
    'Shadow Fang': {},
    'Venom Kiss': { critMult: 0.4, critDmgMult: 0.4, atkMult: 2.3 },
    // wand — Doomwand: SP floor lifted, MP + Crit + CritDmg all boosted
    'Hexrod': {},
    'Doomwand': { spMult: 0.5, mpMult: 1.6, critMult: 2.5, critDmgMult: 2.5 },
    // shuriken — Soulshard: ATK bump + CritDmg ×2 (was ×3 — 30 % crit-damage item was
    //           obsoleting Hexblade Star since the atkMult 1.6 already carried the lean).
    'Hexblade Star': {},
    'Soulshard': { critMult: 0.4, atkMult: 1.6, critDmgMult: 2.0 },
    // throwing knife — Whisper Knife: reliable DPS, ATK 2.3×
    "Reaper's Knife": {},
    'Whisper Knife': { critMult: 0.4, critDmgMult: 0.4, atkMult: 2.3 },
    // bow — Spinereaper: softer ATK cut, crit pair boosted to ×2.5 / ×3.5
    'Wraithbow': {},
    'Spinereaper': { atkMult: 0.6, critMult: 2.5, critDmgMult: 3.5 },
    // spear — Soul-piercer: softer ATK cut, crit pushed to ×3, DEF ×1.8
    'Doomspear': {},
    'Soul-piercer': { atkMult: 0.6, critMult: 3.0, defMult: 1.8 },
    // staff — Doomscepter stays: clean caster swap, no rebalance needed
    'Soulflame Staff': {},
    'Doomscepter': { mpMult: 0.4, spMult: 2.0 },
    // 2H sword — Worldsplitter: mirrors Spinereaper's new crit pair
    "Headsman's Blade": {},
    'Worldsplitter': { atkMult: 0.6, critMult: 2.5, critDmgMult: 3.5 },
    // 2H mace — Worldreaver stays: glass berserker is fine as-is
    'Tombcrusher': {},
    'Worldreaver': { hpMult: 0.4, defMult: 0.4, atkMult: 2.0 },
    // shield — Bulwark of Dread: offense floors stay, DEF + HP pushed to ×2.0
    'Aegis of Bone': {},
    'Bulwark of Dread': { atkMult: 0.3, mpMult: 0.3, spMult: 0.3, critMult: 0.3, defMult: 2.0, hpMult: 2.0 },
    // focus — Souldrain Focus: softer SP / ATK cut, bigger MP + HP payoff
    'Spirit Wisp': {},
    'Souldrain Focus': { spMult: 0.5, atkMult: 0.4, mpMult: 1.8, hpMult: 1.6 },
};
/** Weapon special effects — only applied to Rare and higher weapons */
const WEAPON_EFFECTS = {
    Axe: [
        // Cleave nerfed from 25/35/50 → 15/20/30. Axe + Whirlwind was the dominant
        // combo at high stages (the splash fires on EVERY auto-attack, compounding
        // with Whirlwind's 4-target AoE). Keeping axe's high raw attack + crit-dmg
        // but cutting the splash % brings clear-speed in line without nerfing the
        // weapon's damage per single hit.
        { rare: 'Cleave: +15% AoE damage', epic: 'Cleave: +20% AoE damage', legendary: 'Cleave: +30% AoE damage' },
    ],
    Mace: [
        // 1H Mace single-target stun. Stun duration capped at 1.25 s on
        // legendary per user balance pass — was running 2 s which let a
        // 20 % proc on a fast attacker effectively pin a single elite/boss
        // for half its uptime. Proc chances unchanged.
        { rare: '10% chance to stun on hit for 0.75s', epic: '15% chance to stun for 1s', legendary: '20% chance to stun for 1.25s' },
    ],
    Bow: [
        { rare: 'Venom: 15% chance to poison for 3s', epic: 'Venom: 20% chance to poison for 4s', legendary: 'Venom: 25% chance to poison for 5s' },
        { rare: 'Cripple: 15% chance to slow by 25%', epic: 'Cripple: 20% chance to slow by 35%', legendary: 'Maim: 25% chance to cripple and slow by 40%' },
    ],
    Staff: [
        { rare: 'Arcane Flow: +5% mana regen to party', epic: 'Arcane Flow: +10% mana regen to party', legendary: 'Arcane Flow: +15% mana regen to party' },
        { rare: 'Haste Surge: 8% chance on cast to gain +30% attack speed for 4s', epic: 'Haste Surge: 12% chance on cast to gain +30% attack speed for 4s', legendary: 'Haste Surge: 18% chance on cast to gain +30% attack speed for 4s' },
        { rare: 'Spell Echo: Procs +30 bonus spell damage', epic: 'Spell Echo: Procs +50 bonus spell damage', legendary: 'Spell Echo: Procs +80 bonus spell damage' },
    ],
    Dagger: [
        { rare: 'Lethality: Crits have 10% chance to multiply (x2)', epic: 'Lethality: Crits have 14% chance to multiply (x2-x3)', legendary: 'Lethality: Crits have 16% chance to multiply (x2-x4)' },
    ],
    Sword: [
        { rare: 'Vorpal: 10% chance to deal double damage', epic: 'Vorpal: 12% chance to deal double damage', legendary: 'Vorpal: 15% chance to deal triple damage' },
    ],
    // Runewhisper — caster-leaning sword. Replaces the generic Vorpal proc
    // with a Runic Surge SP-bonus on auto-attacks, fitting Cyrith's hybrid
    // identity (the only sword that wants spell power on it). Looked up by
    // item NAME — Bonecleaver still pulls the Sword pool above.
    // Numbers halved from initial 10/20/30 → 5/10/15 — at SP 200 the original
    // legendary was running ~2.5× peer 1H sword DPS (vs Vorpal's average).
    Runewhisper: [
        { rare: 'Runic Surge: Auto-attacks deal +5% SP as bonus damage', epic: 'Runic Surge: Auto-attacks deal +10% SP as bonus damage', legendary: 'Runic Surge: Auto-attacks deal +15% SP as bonus damage, can spell-crit' },
    ],
    '2H Sword': [
        { rare: '10% chance to triple attack', epic: '20% chance to triple attack', legendary: '30% chance to triple attack' },
    ],
    '2H Mace': [
        // 2H Mace Tremor — battlefield-wide AoE stun. Was way out of line vs
        // the peer 2H Sword's triple-attack (single-target damage). Legendary
        // 30%/2.5s on a 1s cadence pinned the entire field roughly half the
        // time. Proc chances cut ~50% across rarities, durations trimmed.
        { rare: '6% chance to AoE stun all enemies for 1s', epic: '10% chance to AoE stun all enemies for 1.25s', legendary: '14% chance to AoE stun all enemies for 1.5s' },
    ],
    Spear: [
        { rare: 'Pierce: Attacks ignore 15% of enemy armor', epic: 'Pierce: Attacks ignore 25% of enemy armor', legendary: 'Pierce: Attacks ignore 35% of enemy armor' },
    ],
    Shield: [
        { rare: 'Fortify: Block 10% of incoming damage', epic: 'Fortify: Block 15% of incoming damage', legendary: 'Fortify: Block 20% of incoming damage, reflect 5%' },
    ],
    Shuriken: [
        { rare: 'Fan of Blades: Hits 1 additional target at 40% damage', epic: 'Fan of Blades: Hits 2 additional targets at 40% damage', legendary: 'Fan of Blades: Hits 3 additional targets at 40% damage, 10% more crit' },
    ],
    'Throwing Knife': [
        { rare: 'Ranged Lethality: x2.1-x2.3 damage at range', epic: 'Ranged Lethality: x2.3-x2.6 damage at range', legendary: 'Ranged Lethality: x2.6-x3.0 damage at range' },
    ],
    Focus: [
        { rare: 'Conduit: +15% spell power scaling', epic: 'Conduit: +20% spell power scaling', legendary: 'Conduit: +25% spell power, abilities cost 10% less mana' },
        { rare: 'Mana Flow: +10% mana regen', epic: 'Mana Flow: +18% mana regen', legendary: 'Mana Flow: +25% mana regen, +3 mana per cast' },
    ],
    Wand: [
        { rare: 'Quickcast: Reduces spell cast time by 10%', epic: 'Quickcast: Reduces spell cast time by 15%', legendary: 'Quickcast: Reduces cast time by 20%, +5% crit chance for spells' },
        { rare: 'Mana Drain: Hits restore 3 mana per cast', epic: 'Mana Drain: Hits restore 5 mana per cast', legendary: 'Mana Drain: Hits restore 8 mana per cast' },
    ],
};
/** Maps each relic's full name to a unique icon — every relic has its own icon */
export const RELIC_NAME_ICON_MAP = {
    // Offensive
    'Warblade Sigil': 'icon_relic_sigil',
    'Orb of Arcane Fury': 'icon_relic_orb',
    'Emblem of Precision': 'icon_relic_emblem',
    'Idol of Swiftness': 'icon_relic_idol',
    'Talisman of the Fifth': 'icon_relic_talisman',
    'Charm of Twin Strikes': 'icon_relic_charm',
    'Totem of Bloodlust': 'icon_relic_totem',
    // Defensive
    'Aegis of Stoneskin': 'icon_relic_gem',
    'Ward of the Guardian': 'icon_relic_ring',
    'Chalice of Renewal': 'icon_relic_chalice',
    'Bulwark Emblem': 'icon_relic_bulwark',
    'Sigil of Vampirism': 'icon_relic_vampirism',
    // Economy / Utility
    'Goldseer Idol': 'icon_relic_goldseer',
    'Book of Enlightenment': 'icon_relic_tome',
    'Scavenger Charm': 'icon_relic_scavenger',
    'Fortunekeeper Orb': 'icon_relic_fortune',
    // Class-synergy
    'Mindstone Talisman': 'icon_relic_mindstone',
    'Chronoshard': 'icon_relic_skull',
    'Soulspark Sigil': 'icon_relic_soulspark',
    'Manafount Charm': 'icon_relic_manafount',
    // Unique
    'Frostgrasp Totem': 'icon_relic_frostgrasp',
    'Slayer Emblem': 'icon_relic_slayer',
    'Vanguard Ward': 'icon_relic_vanguard',
    'Crescendo Idol': 'icon_relic_crescendo',
    'Vitality Idol': 'icon_relic_vitality',
    "Archon's Focus": 'icon_relic_archon_focus',
    "Spellbinder's Ring": 'icon_relic_spellbinder_ring',
    // Unique-tier relics
    'Resonant Prong': 'icon_unique_resonant_prong',
    'Bloodtally Knife': 'icon_unique_bloodtally_knife',
    'Timestop Pendant': 'icon_unique_timestop_pendant',
    'Storm Capacitor': 'icon_unique_storm_capacitor',
    'Overflow Crystal': 'icon_unique_overflow_crystal',
    "Forgemaster's Ingot": 'icon_unique_forgemasters_ingot',
    'Spellfire Lens': 'icon_unique_spellfire_lens',
    'Echo Catalyst': 'icon_unique_echo_catalyst',
    'Radiant Surge': 'icon_unique_radiant_surge',
    'Mana Flux Crystal': 'icon_unique_mana_flux_crystal',
};
/**
 * Roll a rarity based on weighted probabilities.
 * lootTier increases chance of higher rarities.
 */
/**
 * rarityUpMult — proportional multiplier applied to all non-common rarity weights.
 * 1.0 = no change. 1.10 = each rarity tier is 10% more likely relative to common.
 * Applied multiplicatively: if rare base weight is 10, rarityUpMult 1.10 → weight 11
 * (so rare goes from 10/100 = 10% to ~10.9%, not 10+10 = 20%).
 */
function rollRarity(lootTier, enemyTier, rarityUpMult = 1.0, itemLevel = 1) {
    const weights = { ...RARITY_DROP_WEIGHTS };
    // Enemy tier shifts base weights (enemy quality, not external buffs)
    weights.uncommon += lootTier * 5;
    weights.rare += lootTier * 3;
    weights.epic += lootTier * 1.5;
    // Legendary is intentionally NOT boosted by lootTier here — we override the
    // weight per-tier below once stage 20+ unlocks, aiming for a tight target
    // rate (~0.4 % elite, ~0.9 % boss) rather than letting it scale with mob
    // quality. The `weights.legendary` default from RARITY_DROP_WEIGHTS (1) is
    // only a placeholder until the tier-specific overrides run.
    if (enemyTier === 'minion') {
        weights.epic = 0;
        weights.legendary = 0;
        // Further stage-gate minion drops so the first zones stay on the common/
        // uncommon rung — stage 1-4 mobs don't drop rare either. Rare unlocks at
        // stage 5+, matching the progression curve for elites/bosses' minRarity.
        if (itemLevel < 5)
            weights.rare = 0;
    }
    // Stage-gate epic drops so early heroes can't luck into a carry-item:
    //   epic → stage 10+ only
    // Rolls that would have hit a locked tier get zeroed and fall through to the next-lower
    // tier.
    if (itemLevel < 10)
        weights.epic = 0;
    // Legendary gate + tier-specific rate:
    //   <20        → 0 %
    //   20+ elite  → ~0.4 %  (legendary weight 0.48 / total ~118.6)
    //   20+ boss   → ~0.9 %  (legendary weight 1.35 / total ~148 pre-clamp)
    //   20+ minion → 0 %     (minions never drop legendary — set above)
    if (itemLevel < 20) {
        weights.legendary = 0;
    }
    else if (enemyTier === 'elite') {
        weights.legendary = 0.48;
    }
    else if (enemyTier === 'boss') {
        weights.legendary = 1.35;
    }
    // Proportional rarity boost (Loot tome etc.) — scales all non-common weights by the same factor
    // so +10% tome means each non-common rarity is 10% more likely, not +10 flat weight points
    if (rarityUpMult !== 1.0) {
        weights.uncommon *= rarityUpMult;
        weights.rare *= rarityUpMult;
        weights.epic *= rarityUpMult;
        weights.legendary *= rarityUpMult;
        weights.unique *= rarityUpMult;
    }
    const total = weights.common + weights.uncommon + weights.rare + weights.epic + weights.legendary + weights.unique;
    let roll = Math.random() * total;
    for (const rarity of RARITY_LIST) {
        roll -= weights[rarity];
        if (roll <= 0)
            return rarity;
    }
    return 'common';
}
const RELIC_POOL = [
    // Offensive
    { name: 'Warblade Sigil', effect: 'Auto attacks deal +15% bonus damage' },
    { name: 'Orb of Arcane Fury', effect: 'Spell damage increased by 12%' },
    { name: 'Emblem of Precision', effect: 'Critical strikes deal +25% extra damage' },
    { name: 'Idol of Swiftness', effect: '+8% attack speed' },
    { name: 'Talisman of the Fifth', effect: 'Every 5th attack deals double damage' },
    { name: 'Charm of Twin Strikes', effect: 'Attacks have 10% chance to strike twice' },
    { name: 'Totem of Bloodlust', effect: 'Kills grant +20% damage for 5s' },
    // Defensive
    { name: 'Aegis of Stoneskin', effect: 'Reduce incoming damage by 8%' },
    { name: 'Ward of the Guardian', effect: 'Taking damage has 15% × s chance to shield for 8-30% of max HP (4 s ICD; scales 0.5% per relic level, capped 30% at lv 50)' },
    { name: 'Chalice of Renewal', effect: '+0.5-2% of max HP regen per second in combat (scales 0.03% per relic level, capped at 2% at lv 50)' },
    { name: 'Bulwark Emblem', effect: 'Block 15% of damage when above 50% HP' },
    { name: 'Sigil of Vampirism', effect: 'On hit: 10% chance to heal for 5% max HP' },
    // Utility / Economy
    { name: 'Goldseer Idol', effect: '+20% gold from kills' },
    { name: 'Book of Enlightenment', effect: '+15% XP from all sources' },
    { name: 'Scavenger Charm', effect: 'Enemies drop +10% more loot' },
    { name: 'Fortunekeeper Orb', effect: '+10% loot drop chance' },
    // Class-synergy
    { name: 'Mindstone Talisman', effect: 'Abilities cost 10% less resource' },
    { name: 'Chronoshard', effect: 'Ability cooldowns reduced by 8%' },
    { name: 'Soulspark Sigil', effect: 'Critical strikes restore 5 resource' },
    { name: 'Manafount Charm', effect: 'Gain 3 mana on each auto-attack' },
    // Unique
    { name: 'Frostgrasp Totem', effect: 'Nearby enemies move 10% slower — +1% per relic level past 10 (max 50% at lv 50)' },
    { name: 'Slayer Emblem', effect: '+25% attack speed for 5s after killing an enemy' },
    { name: 'Vanguard Ward', effect: 'Start each wave with a shield equal to 12% of max HP' },
    { name: 'Crescendo Idol', effect: 'Gain 1% damage for each wave cleared (max 50 stacks, accumulates across all zones)' },
    { name: 'Vitality Idol', effect: 'Gain +0.2% max HP per kill (max 500 stacks → +100% HP, resets on death)' },
    // Caster-flavoured parallels to Crescendo (ATK) + Twin Strikes (ATK). Added 2026-04-24 to
    // rebalance the relic pool after a pass showed ~2× more ATK-exclusive relics than SP ones.
    { name: "Archon's Focus", effect: 'Gain +1% spell power per kill. Max stacks scale with rarity (rare 50 / epic 75 / legendary 99). Resets on death.' },
    { name: "Spellbinder's Ring", effect: 'Spells have a 10% chance to be cast twice' },
];
/**
 * Unique-tier relics — effect only, no stat bonuses, 5-token recycle value.
 * Drop chance: ~6% when an epic/legendary relic would drop.
 */
const UNIQUE_RELIC_POOL = [
    { name: 'Resonant Prong', effect: 'Critical hits deal their damage a second time at 50% value.' },
    { name: 'Bloodtally Knife', effect: '5% chance on kill to permanently gain +1% attack (stacks up to +100% per run).' },
    { name: 'Timestop Pendant', effect: 'Once per 90s: prevents lethal damage on any hero and freezes all enemies for 4s.' },
    { name: 'Storm Capacitor', effect: '25% chance on auto-attack to arc lightning to a random enemy for 80% attack damage.' },
    { name: 'Overflow Crystal', effect: 'Doubles max Rage/Energy/Mana. Resource regeneration increased by 80%.' },
    { name: "Forgemaster's Ingot", effect: 'All equipped weapons on this hero grant +40% of their stat bonuses.' },
    { name: 'Spellfire Lens', effect: 'Spell damage multiplied by (1 + SP/200). At 200 SP this doubles all spell damage.' },
    { name: 'Echo Catalyst', effect: "25% chance after casting a spell to echo it — deals 50% of hero's spell power as bonus arcane damage." },
    { name: 'Radiant Surge', effect: 'After the healer casts a heal, the target gains +80% damage for 3 seconds.' },
    { name: 'Mana Flux Crystal', effect: 'When mana/energy is full, the next ability deals +80% damage and drains all resource.' },
];
/** Maps rarity to its index in WEAPON_ICON_MAP arrays (one icon per rarity) */
const RARITY_IDX = {
    common: 0, uncommon: 1, rare: 2, epic: 3, legendary: 4, unique: 5,
};
/** One chest icon per (renamed) armor name — reuses the old icon files since
 *  dedicated art for the darker palette hasn't been drawn yet. Sub-type +
 *  lean still drive stat differentiation; this is purely visual flavor. */
const ARMOR_NAME_ICON_MAP = {
    // Plate (heavy)
    'Wraith Mail': 'icon_armor_chainmail',
    'Dread Plate': 'icon_armor_plate_armor',
    'Ironwarden': 'icon_armor_war_plate',
    // Brigandine (heavy)
    'Bonecage': 'icon_armor_brigandine',
    'Grim Hauberk': 'icon_armor_brigandine',
    // Skirmisher (medium)
    'Serpentskin': 'icon_armor_scale_mail',
    'Spiked Hide': 'icon_armor_studded_leather',
    'Bloodharness': 'icon_armor_battle_harness',
    // Hunter (medium)
    "Stalker's Vest": 'icon_armor_leather_vest',
    "Reaper's Coat": 'icon_armor_hunters_coat',
    'Shadewalker Garb': 'icon_armor_ranger_vest',
    // Robe (light)
    'Ashen Robe': 'icon_armor_robe',
    'Whisper Silk': 'icon_armor_silk_robe',
    // Vestment (light)
    'Dreadweave Vestment': 'icon_armor_robe',
    "Heretic's Habit": 'icon_armor_silk_robe',
    'Cursed Mystic': 'icon_armor_robe',
};
/** Resolve the best sprite icon key for an equipment item */
function getSpriteKey(slot, name, isRelic, rarity) {
    if (isRelic) {
        if (RELIC_NAME_ICON_MAP[name])
            return RELIC_NAME_ICON_MAP[name];
        return 'icon_relic_voodoo'; // fallback
    }
    if (slot === 'weapon1' || slot === 'weapon2') {
        const ri = RARITY_IDX[rarity ?? 'common'] ?? 0;
        // First: flavor-name lookup (post-rename items). Map the flavor name to its
        // weapon type, then to the legacy WEAPON_ICON_MAP key.
        const wt = WEAPON_NAME_TYPE[name];
        if (wt) {
            for (const [legacyKey, ltype] of Object.entries(NAME_TO_WEAPON_TYPE)) {
                if (ltype === wt && WEAPON_ICON_MAP[legacyKey]) {
                    return WEAPON_ICON_MAP[legacyKey][Math.min(ri, WEAPON_ICON_MAP[legacyKey].length - 1)];
                }
            }
        }
        // Legacy fallback: substring match on old prefixed names ("Sturdy Sword").
        for (const [weaponName, icons] of Object.entries(WEAPON_ICON_MAP)) {
            if (name.includes(weaponName)) {
                return icons[Math.min(ri, icons.length - 1)];
            }
        }
        return 'icon_sword_c'; // fallback
    }
    if (slot === 'armor') {
        // Exact name match first
        if (ARMOR_NAME_ICON_MAP[name])
            return ARMOR_NAME_ICON_MAP[name];
        // Partial match fallback (handles prefixed names like "Arcane Studded Leather")
        for (const [armorName, iconKey] of Object.entries(ARMOR_NAME_ICON_MAP)) {
            if (name.includes(armorName))
                return iconKey;
        }
        return 'icon_armor_heavy_c'; // final fallback
    }
    return 'icon_sword_c';
}
/**
 * Re-compute the spriteKey for an armor item using the current ARMOR_NAME_ICON_MAP.
 * Called during save migration to fix items generated with old rarity-suffix icon keys.
 */
export function repairArmorSpriteKey(item) {
    if (item.slot !== 'armor')
        return;
    item.spriteKey = getSpriteKey('armor', item.name, false, item.rarity);
}
/**
 * Re-compute the spriteKey for a weapon/shield item using the current WEAPON_ICON_MAP.
 * Needed for items saved before shield icons were added.
 */
export function repairWeaponSpriteKey(item) {
    if (item.slot !== 'weapon1' && item.slot !== 'weapon2')
        return;
    item.spriteKey = getSpriteKey(item.slot, item.name, false, item.rarity);
}
/**
 * Generate a random equipment item.
 * Relics are rare+ only and always have a special effect.
 * Armor rolls as heavy (tank) or light (squishy).
 *
 * `minRarity` clamps the rolled rarity upward only (used by boss drops).
 * `exactRarity`, when provided, skips the rarity roll entirely and fixes
 * the item at that exact tier — used by mystery-box / merchant paths that
 * pre-decide rarity. Previously those callers overwrote `.rarity` after
 * generation, which left name/stats/effect at the rolled tier while the
 * label stamped down — producing "common Eternal Sword with +188 ATK and
 * legendary Vorpal" mismatches. exactRarity threads the decision all the
 * way through so every derived field matches.
 */
export function generateEquipment(itemLevel, lootTier, enemyTier = 'elite', minRarity, rarityUpMult = 1.0, rareLootUpgrade = 0, exactRarity) {
    // Weighted slot selection: weapons/armor each 3×, relics each 1× (relic total < weapon or armor)
    const totalWeight = SLOT_WEIGHTS.reduce((s, w) => s + w, 0);
    let slotRoll = Math.random() * totalWeight;
    let slotIdx = 0;
    for (let i = 0; i < SLOT_WEIGHTS.length; i++) {
        slotRoll -= SLOT_WEIGHTS[i];
        if (slotRoll <= 0) {
            slotIdx = i;
            break;
        }
    }
    let slot = SLOT_LIST[slotIdx];
    let rarity;
    if (exactRarity) {
        // Exact mode — skip the roll entirely. Everything downstream (prefix, stats, effect)
        // keys off this rarity so there are no mismatches.
        rarity = exactRarity;
    }
    else {
        rarity = rollRarity(lootTier, enemyTier, rarityUpMult, itemLevel);
        // Lucky Draw (rune) — chance to upgrade a rare roll → epic
        if (rarity === 'rare' && rareLootUpgrade > 0 && Math.random() < rareLootUpgrade) {
            rarity = 'epic';
        }
        // Clamp rarity up to minimum if specified
        if (minRarity) {
            const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'];
            if (rarityOrder.indexOf(rarity) < rarityOrder.indexOf(minRarity))
                rarity = minRarity;
        }
    }
    const isRelic = slot.startsWith('relic');
    // Weapons & armor cannot be unique tier — cap at legendary
    if (!isRelic && rarity === 'unique')
        rarity = 'legendary';
    // Relics must be rare (blue) or higher — upgrade if common/uncommon (never downgrade unique)
    if (isRelic && rarity !== 'unique') {
        const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
        if (rarityOrder.indexOf(rarity) < 2)
            rarity = 'rare';
    }
    // Unique-tier relic: rolled directly from RARITY_DROP_WEIGHTS (weight 0.05)
    if (isRelic && rarity === 'unique') {
        const uniqueDef = UNIQUE_RELIC_POOL[Math.floor(Math.random() * UNIQUE_RELIC_POOL.length)];
        return {
            id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            name: uniqueDef.name,
            slot,
            rarity: 'unique',
            classRestriction: null,
            level: 1,
            statBonuses: {},
            specialEffect: uniqueDef.effect,
            sellValue: RARITY_SELL_MULT['unique'],
            spriteKey: getSpriteKey(slot, uniqueDef.name, true),
            levelRequirement: itemLevel,
        };
    }
    const mult = RARITY_STAT_MULT[rarity];
    // Pick relic from fixed pool (name + effect are paired), or random name for other slots
    let relicDef;
    let name;
    if (isRelic) {
        relicDef = RELIC_POOL[Math.floor(Math.random() * RELIC_POOL.length)];
        name = relicDef.name;
    }
    else if (slot === 'armor' || slot === 'weapon1' || slot === 'weapon2') {
        // Armor + weapons use their base name only — flavor names are unique per
        // sub-type/lean, so rarity color + icon do the rarity signaling. Prefix
        // ("Sturdy Bonecleaver") would just be redundant noise.
        const names = ITEM_NAMES[slot];
        name = names[Math.floor(Math.random() * names.length)];
    }
    else {
        const names = ITEM_NAMES[slot];
        const prefixes = RARITY_PREFIXES[rarity];
        name = `${prefixes[Math.floor(Math.random() * prefixes.length)]} ${names[Math.floor(Math.random() * names.length)]}`;
    }
    // Generate stat bonuses based on slot and level (v6.0: x2 item stats for hero scaling)
    // After stage 30, items receive an accelerating power boost (~2.5% compound per level)
    // so full-geared heroes remain competitive against rapidly-scaling mobs
    const lateBoost = itemLevel > 30 ? Math.pow(1.025, itemLevel - 30) : 1;
    const basePower = (2 + itemLevel * 1.5) * 2 * lateBoost;
    const statBonuses = {};
    let armorArchetype;
    let armorSubtype;
    // Resolve weapon type: new flavor-name lookup first, fall back to legacy
    // suffix scan so saved items rolled before the rename ("Sturdy Sword") still
    // resolve correctly. baseWeaponName is the legacy type word ("Sword", "2H Mace")
    // — used downstream for WEAPON_EFFECTS pool keying and shield/focus checks.
    let weaponType;
    let baseWeaponName = '';
    if (!isRelic) {
        weaponType = WEAPON_NAME_TYPE[name];
        if (weaponType) {
            // Reverse-lookup: pick the matching legacy key for WEAPON_EFFECTS / shield / focus checks.
            for (const [key, t] of Object.entries(NAME_TO_WEAPON_TYPE)) {
                if (t === weaponType) {
                    baseWeaponName = key;
                    break;
                }
            }
        }
        else {
            // Legacy fallback for old prefixed names ("Sturdy Sword", "Eternal 2H Mace").
            for (const key of Object.keys(NAME_TO_WEAPON_TYPE).sort((a, b) => b.length - a.length)) {
                if (name.endsWith(key)) {
                    baseWeaponName = key;
                    break;
                }
            }
            if (!baseWeaponName)
                baseWeaponName = name.split(' ').pop() || '';
            weaponType = NAME_TO_WEAPON_TYPE[baseWeaponName];
        }
    }
    const isShield = baseWeaponName === 'Shield';
    const isFocus = baseWeaponName === 'Focus';
    // 2H weapons (2H Sword, 2H Mace) must be at least rare. If we upgraded the tier, also
    // regenerate the prefix so a 2H Sword rolled at uncommon doesn't end up as "Sturdy 2H Sword"
    // (uncommon prefix) with a blue rare-tier color in the inventory — cause of the "Sturdy
    // shown in purple" reports.
    if (weaponType === 'sword_2h' || weaponType === 'mace_2h') {
        const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary', 'unique'];
        if (rarityOrder.indexOf(rarity) < rarityOrder.indexOf('rare')) {
            rarity = 'rare';
            const prefixes = RARITY_PREFIXES[rarity];
            name = `${prefixes[Math.floor(Math.random() * prefixes.length)]} ${baseWeaponName}`;
        }
    }
    switch (slot) {
        case 'weapon1':
        case 'weapon2':
            if (isShield) {
                // Shield: common gets defense + hp only (2 stats). uncommon+ pick up attack + mp,
                // rare+ add crit chance, epic+ add spell power. Common rolling 4 stats previously
                // felt off — commons should be vanilla fillers.
                statBonuses.defense = Math.round(basePower * mult * 0.9);
                statBonuses.hp = Math.round(basePower * mult * 3);
                if (rarity !== 'common') {
                    statBonuses.attack = Math.round(basePower * mult * 0.2);
                    statBonuses.mp = Math.round(basePower * mult * 0.6);
                }
                if (rarity !== 'common' && rarity !== 'uncommon')
                    statBonuses.spellPower = Math.round(basePower * mult * 0.3);
                if (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') {
                    statBonuses.critChance = +(0.008 * mult * (0.8 + Math.random() * 0.4)).toFixed(3);
                }
            }
            else if (isFocus) {
                // Off-Hand Focus: common gets spellPower + mp only (2 stats). uncommon+ adds attack,
                // rare+ adds hp. Keeps the commons minimal while the dual-wield scaling to ~2H staff
                // kicks in at higher rarities.
                statBonuses.spellPower = Math.round(basePower * mult * 1.2);
                statBonuses.mp = Math.round(basePower * mult * 1.8);
                if (rarity !== 'common')
                    statBonuses.attack = Math.round(basePower * mult * 0.4);
                if (rarity !== 'common' && rarity !== 'uncommon')
                    statBonuses.hp = Math.round(basePower * mult * 1.5);
            }
            else {
                // Per weapon-type stat pools
                switch (weaponType) {
                    case 'staff':
                        // 2H caster — occupies both weapon slots; stats × 2 to match wand+focus combined
                        statBonuses.mp = Math.round(basePower * mult * 1.8 * 2.0);
                        if (rarity !== 'common') {
                            statBonuses.spellPower = Math.round(basePower * mult * (0.9 + Math.random() * 0.4) * 2.0);
                        }
                        if (rarity === 'epic' || rarity === 'legendary')
                            statBonuses.critDamage = +(0.08 * mult * 2.0).toFixed(2);
                        break;
                    case 'wand':
                        // One-handed caster — mana always; spellPower only at rare+. critChance bumped
                        // 0.006 → 0.015 (2.5 ×) so epic/legendary actually feel crit-capable (was
                        // 1.2 % at epic, rounded to "+1 %" in tooltip — dead stat).
                        statBonuses.mp = Math.round(basePower * mult * 1.2);
                        if (rarity !== 'common' && rarity !== 'uncommon') {
                            statBonuses.spellPower = Math.round(basePower * mult * (0.7 + Math.random() * 0.3));
                        }
                        if (rarity === 'epic' || rarity === 'legendary')
                            statBonuses.critChance = +(0.015 * mult).toFixed(3);
                        break;
                    case 'bow':
                        // 2H ranged crit — attack + critChance + critDamage all scale × 2 to match
                        // dual-wield combined. critDmg was previously pinned at 1H scale (0.03 × mult)
                        // to avoid the legendary Spinereaper +120 % critDmg compound bug, but that left
                        // bows with single-digit critDmg vs every other 2H weapon's 15-50 % range —
                        // clearly a dead stat. Bumped base to 0.08 and restored the × 2 2H scaling.
                        statBonuses.attack = Math.round(basePower * mult * (0.7 + Math.random() * 0.3) * 2.0);
                        statBonuses.critChance = +(0.015 * mult * (1 + Math.random() * 0.5) * 2.0).toFixed(3);
                        if (rarity === 'epic' || rarity === 'legendary')
                            statBonuses.critDamage = +(0.08 * mult * 2.0).toFixed(2);
                        break;
                    case 'dagger':
                        // Assassin — highest crit chance + crit damage
                        statBonuses.attack = Math.round(basePower * mult * (0.6 + Math.random() * 0.3));
                        statBonuses.critChance = +(0.018 * mult * (1 + Math.random() * 0.5)).toFixed(3);
                        if (rarity !== 'common')
                            statBonuses.critDamage = +(0.12 * mult * (0.8 + Math.random() * 0.4)).toFixed(2);
                        break;
                    case 'shuriken':
                        // Fast assassin — crit focus
                        statBonuses.attack = Math.round(basePower * mult * (0.5 + Math.random() * 0.3));
                        statBonuses.critChance = +(0.016 * mult * (1 + Math.random() * 0.5)).toFixed(3);
                        if (rarity !== 'common')
                            statBonuses.critDamage = +(0.10 * mult * (0.8 + Math.random() * 0.4)).toFixed(2);
                        break;
                    case 'throwing_knife':
                        // Ranged assassin — crit focus
                        statBonuses.attack = Math.round(basePower * mult * (0.6 + Math.random() * 0.3));
                        statBonuses.critChance = +(0.016 * mult * (1 + Math.random() * 0.5)).toFixed(3);
                        if (rarity !== 'common')
                            statBonuses.critDamage = +(0.11 * mult * (0.8 + Math.random() * 0.4)).toFixed(2);
                        break;
                    case 'axe':
                        // Highest raw attack, heavy critDamage
                        statBonuses.attack = Math.round(basePower * mult * (1.1 + Math.random() * 0.3));
                        if (rarity !== 'common')
                            statBonuses.critDamage = +(0.10 * mult).toFixed(2);
                        break;
                    case 'mace':
                        // Bruiser — attack + defense + HP
                        statBonuses.attack = Math.round(basePower * mult * (0.9 + Math.random() * 0.2));
                        statBonuses.defense = Math.round(basePower * mult * 0.4);
                        if (rarity !== 'common')
                            statBonuses.hp = Math.round(basePower * mult * 1.0);
                        break;
                    case 'spear':
                        // 2H reach — attack + crit + defense; stats × 2 to match dual-wield combined.
                        // critChance base bumped 0.008 → 0.015 so the rolled value actually matters,
                        // and the 40 % "didn't roll" coin-flip removed so the stat is predictable.
                        statBonuses.attack = Math.round(basePower * mult * (0.9 + Math.random() * 0.3) * 2.0);
                        statBonuses.critChance = +(0.015 * mult * (1 + Math.random() * 0.4) * 2.0).toFixed(3);
                        if (rarity !== 'common')
                            statBonuses.defense = Math.round(basePower * mult * 0.3 * 2.0);
                        break;
                    case 'sword_2h':
                        // 2H Sword — sword stats × 2.1
                        statBonuses.attack = Math.round(basePower * mult * (0.8 + Math.random() * 0.3) * 2.1);
                        if (Math.random() > 0.4)
                            statBonuses.critChance = +(0.008 * mult * (1 + Math.random()) * 2.1).toFixed(3);
                        if (rarity === 'epic' || rarity === 'legendary')
                            statBonuses.critDamage = +(0.08 * mult * 2.1).toFixed(2);
                        break;
                    case 'mace_2h':
                        // 2H Mace — mace stats × 2.1
                        statBonuses.attack = Math.round(basePower * mult * (0.9 + Math.random() * 0.2) * 2.1);
                        statBonuses.defense = Math.round(basePower * mult * 0.4 * 2.1);
                        if (rarity !== 'common')
                            statBonuses.hp = Math.round(basePower * mult * 1.0 * 2.1);
                        break;
                    case 'sword':
                    default: {
                        // 1H sword has two named variants: Bonecleaver (fighter) and
                        // Runewhisper (caster). The lean's `forceRuneblade` flag locks the
                        // caster roll for Runewhisper; without it (Bonecleaver) we always
                        // roll fighter. Old saved "Sword" items fall through to the legacy
                        // 50/50 random — kept for back-compat.
                        const wlean = WEAPON_NAME_LEAN[name];
                        const explicitRuneblade = wlean?.forceRuneblade === true;
                        const explicitFighter = wlean !== undefined && !explicitRuneblade;
                        const isRuneblade = explicitRuneblade
                            || (!explicitFighter && Math.random() < 0.5);
                        if (isRuneblade) {
                            statBonuses.spellPower = Math.round(basePower * mult * (0.8 + Math.random() * 0.3));
                        }
                        else {
                            statBonuses.attack = Math.round(basePower * mult * (0.8 + Math.random() * 0.3));
                        }
                        // critChance base bumped 0.008 → 0.015 + 40 % skip gate removed — roll was
                        // landing at 1 % on common swords which rounded to a useless "+1 %" display.
                        // critDmg epic+ bumped 0.08 → 0.10 so the high-rarity sword roll is meaningful.
                        statBonuses.critChance = +(0.015 * mult * (1 + Math.random())).toFixed(3);
                        if (rarity === 'epic' || rarity === 'legendary')
                            statBonuses.critDamage = +(0.10 * mult).toFixed(2);
                        break;
                    }
                }
            }
            // ── Per-name lean (weapon) ──
            // Drop-and-boost design: a multiplier < 1 REDUCES a rolled stat (but
            // keeps it present — "always add ATK, just smaller"); a multiplier > 1
            // BOOSTS it. Each variant pair becomes a clear "this OR that" archetype
            // split — e.g. Bloodfang halves ATK, doubles CritDmg.
            //
            // Seeding: some base-roll slots are gated by rarity (e.g. critDmg on
            // 2H sword only rolls at epic+). When a lean targets such a stat on a
            // lower-rarity roll the base stat is 0, and the multiplier had nothing
            // to multiply — so a rare Worldsplitter ended up with half-ATK and no
            // crit specialty at all. We now seed a midpoint baseline whenever the
            // lean would BOOST (mult > 1) a stat the base roll left empty.
            {
                const wlean = WEAPON_NAME_LEAN[name];
                if (wlean) {
                    const seedBaseline = (key) => {
                        switch (key) {
                            case 'critChance': return 0.008 * mult; // ~ pre-multiplier
                            case 'critDamage': return 0.08 * mult;
                            case 'attack': return Math.round(basePower * mult * 0.8);
                            case 'spellPower': return Math.round(basePower * mult * 0.8);
                            case 'hp': return Math.round(basePower * mult * 1.0);
                            case 'mp': return Math.round(basePower * mult * 0.6);
                            case 'defense': return Math.round(basePower * mult * 0.4);
                            default: return null;
                        }
                    };
                    const apply = (key, leanMult, fixed = 0) => {
                        if (leanMult === undefined)
                            return;
                        if (leanMult === 0) {
                            delete statBonuses[key];
                            return;
                        }
                        let v = statBonuses[key];
                        if (typeof v !== 'number' || v === 0) {
                            // Reducing a missing stat is a noop; only boost paths seed a baseline.
                            if (leanMult <= 1)
                                return;
                            const seeded = seedBaseline(key);
                            if (seeded === null || seeded === 0)
                                return;
                            v = seeded;
                        }
                        const next = v * leanMult;
                        // Threshold clamp — if a lean multiplier shrinks a stat below a meaningful
                        // display value (e.g. Bulwark of Dread's spMult 0.3 reducing shield SP from 3
                        // to 0.9 → rounds to "+1 SP"), drop the stat entirely so the item's tooltip
                        // isn't polluted with dead single-digit bonuses. Fractional stats (crit %) use
                        // a 1 % floor; integer stats use a +2 floor.
                        if (fixed > 0) {
                            if (next < 0.01) {
                                delete statBonuses[key];
                                return;
                            }
                            statBonuses[key] = +next.toFixed(fixed);
                        }
                        else {
                            const rounded = Math.round(next);
                            if (rounded < 2) {
                                delete statBonuses[key];
                                return;
                            }
                            statBonuses[key] = rounded;
                        }
                    };
                    apply('defense', wlean.defMult);
                    apply('hp', wlean.hpMult);
                    apply('attack', wlean.atkMult);
                    apply('spellPower', wlean.spMult);
                    apply('mp', wlean.mpMult);
                    apply('critChance', wlean.critMult, 3);
                    apply('critDamage', wlean.critDmgMult, 2);
                }
            }
            // Epic+ weapons (not shield/focus) also roll a bonus resistance type — additive, never replaces other stats
            if ((rarity === 'epic' || rarity === 'legendary') && !isShield && !isFocus) {
                const resistTypes = ['fireResist', 'frostResist', 'shadowResist', 'natureResist'];
                const pickedR = resistTypes[Math.floor(Math.random() * resistTypes.length)];
                const maxRoll = rarity === 'legendary' ? 12 : 8;
                statBonuses[pickedR] = Math.round(3 + Math.random() * (maxRoll - 3));
            }
            break;
        case 'armor': {
            // Sub-type lookup — exact-match against ARMOR_NAME_SUBTYPE so each of the
            // 16 named armors lands in the right stat profile reliably (no token-based
            // detection that could miss e.g. "Wraith Mail" → plate). Anything that
            // somehow falls through (shouldn't happen with the curated pool) random-rolls.
            const ALL_SUBTYPES = ['plate', 'brigandine', 'skirmisher', 'hunter', 'robe', 'vestment'];
            const subtype = ARMOR_NAME_SUBTYPE[name]
                ?? ALL_SUBTYPES[Math.floor(Math.random() * ALL_SUBTYPES.length)];
            // Centralised sub-type → archetype lookup so the equip/restrict logic keeps
            // working unchanged via `armorArchetype`.
            const arch = ARMOR_SUBTYPE_ARCHETYPE[subtype];
            armorArchetype = arch;
            armorSubtype = subtype;
            // ── Stat profiles per sub-type ──
            switch (subtype) {
                case 'plate': {
                    // Plate — pure tank. Defensive bulwark: highest DEF/HP, no offense.
                    //   Rare+ unlocks Block %; Epic+ adds resist roll.
                    statBonuses.defense = Math.round(basePower * mult * 1.0);
                    statBonuses.hp = Math.round(basePower * mult * 4.5);
                    if (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') {
                        // Block: rare 6% / epic 8% / legendary 12% (mult-scaled within rarity bracket)
                        statBonuses.blockPct = +(0.04 * mult).toFixed(3);
                    }
                    break;
                }
                case 'brigandine': {
                    // Brigandine — heavy bruiser. Trades a slice of HP/DEF for raw ATK and CritDmg.
                    statBonuses.defense = Math.round(basePower * mult * 0.8);
                    statBonuses.hp = Math.round(basePower * mult * 3.5);
                    statBonuses.attack = Math.round(basePower * mult * 0.4);
                    if (rarity === 'epic' || rarity === 'legendary') {
                        statBonuses.critDamage = +(0.05 * mult).toFixed(2);
                    }
                    break;
                }
                case 'skirmisher': {
                    // Skirmisher — high-burst medium. Crit/CritDmg focused.
                    statBonuses.defense = Math.round(basePower * mult * 0.5);
                    statBonuses.hp = Math.round(basePower * mult * 2.2);
                    statBonuses.attack = Math.round(basePower * mult * 0.5);
                    if (rarity !== 'common')
                        statBonuses.critChance = +(0.012 * mult * (0.8 + Math.random() * 0.4)).toFixed(3);
                    if (rarity === 'epic' || rarity === 'legendary') {
                        statBonuses.critDamage = +(0.05 * mult).toFixed(2);
                    }
                    break;
                }
                case 'hunter': {
                    // Hunter — sustained-DPS medium. Attack-speed % is the defining stat.
                    //   Common: DEF / HP / ATK. Uncommon+: + AtkSpd %. Rare+: + MP for ranger spells.
                    statBonuses.defense = Math.round(basePower * mult * 0.6);
                    statBonuses.hp = Math.round(basePower * mult * 2.5);
                    statBonuses.attack = Math.round(basePower * mult * 0.3);
                    if (rarity !== 'common') {
                        // 0.4 % per mult: common 0 / unc ~0.5 / rare ~0.6 / epic ~0.8 / leg ~1.2 % atk-speed
                        statBonuses.attackSpeedPct = +(0.4 * mult).toFixed(2);
                    }
                    if (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') {
                        statBonuses.mp = Math.round(basePower * mult * 0.4);
                    }
                    break;
                }
                case 'robe': {
                    // Robe — pure caster. Mana sustain + cooldown reduction.
                    //   Common: DEF / HP / SP. Uncommon+: + MP. Rare+: + CDR. Epic+: + Mana Regen.
                    statBonuses.defense = Math.round(basePower * mult * 0.3);
                    statBonuses.hp = Math.round(basePower * mult * 1.5);
                    statBonuses.spellPower = Math.round(basePower * mult * 1.0);
                    if (rarity !== 'common')
                        statBonuses.mp = Math.round(basePower * mult * 1.6);
                    if (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') {
                        // CDR: 0.5 % per mult — rare ~0.8 / epic ~1.0 / leg ~1.5 % CDR
                        statBonuses.cdrPct = +(0.005 * mult).toFixed(3);
                    }
                    if (rarity === 'epic' || rarity === 'legendary') {
                        // Mana regen: epic ~0.4 / leg ~0.6 of basePower → ~9 mp/s leg at lvl 30
                        statBonuses.mpRegenPerSec = Math.round(basePower * mult * 0.05);
                    }
                    break;
                }
                case 'vestment': {
                    // Vestment — battle-mage. Spell crit + crit damage focus.
                    //   Common: DEF / HP / SP. Uncommon+: + MP. Rare+: + Crit. Epic+: + CritDmg.
                    statBonuses.defense = Math.round(basePower * mult * 0.3);
                    statBonuses.hp = Math.round(basePower * mult * 1.5);
                    statBonuses.spellPower = Math.round(basePower * mult * 0.9);
                    if (rarity !== 'common')
                        statBonuses.mp = Math.round(basePower * mult * 1.4);
                    if (rarity === 'rare' || rarity === 'epic' || rarity === 'legendary') {
                        statBonuses.critChance = +(0.006 * mult).toFixed(3);
                    }
                    if (rarity === 'epic' || rarity === 'legendary') {
                        statBonuses.critDamage = +(0.05 * mult).toFixed(2);
                    }
                    break;
                }
            }
            // ── Per-name lean (armor) ──
            // Drop-and-boost design: multiplier 0 = delete stat from item; > 1 = boost.
            // Each variant pair becomes a hard archetype split — e.g. Wraith Mail
            // drops HP entirely and amps DEF, Bloodharness drops DEF and stacks crit.
            {
                const lean = ARMOR_NAME_LEAN[name];
                if (lean) {
                    const apply = (key, mult, fixed = 0) => {
                        if (mult === undefined)
                            return;
                        if (mult === 0) {
                            delete statBonuses[key];
                            return;
                        }
                        const v = statBonuses[key];
                        if (typeof v !== 'number')
                            return;
                        const next = v * mult;
                        statBonuses[key] = fixed > 0 ? +next.toFixed(fixed) : Math.round(next);
                    };
                    apply('defense', lean.defMult);
                    apply('hp', lean.hpMult);
                    apply('attack', lean.atkMult);
                    apply('spellPower', lean.spMult);
                    apply('mp', lean.mpMult);
                    apply('critChance', lean.critMult, 3);
                    apply('critDamage', lean.critDmgMult, 2);
                    apply('attackSpeedPct', lean.atkSpdMult, 2);
                    apply('blockPct', lean.blockMult, 3);
                }
            }
            // Epic+ armor rolls one random resistance type — flat range, no rarity mult
            // Max ~25% from armor so reaching 50%+ requires a Mimic trophy
            if (rarity === 'epic' || rarity === 'legendary') {
                const resistTypes = ['fireResist', 'frostResist', 'shadowResist', 'natureResist'];
                const picked = resistTypes[Math.floor(Math.random() * resistTypes.length)];
                const maxRoll = rarity === 'legendary' ? 25 : 16;
                statBonuses[picked] = Math.round(4 + Math.random() * (maxRoll - 4));
            }
            break;
        }
        case 'relic1':
        case 'relic2':
        case 'relic3':
        case 'relic4': {
            // Relics give moderate mixed stats + always have a unique passive
            // Scale slightly more with level: +4% bonus per level above 1
            const relicLevelScale = 1 + (itemLevel - 1) * 0.04;
            const possibleStats = ['attack', 'defense', 'hp', 'mp', 'critChance', 'critDamage', 'spellPower'];
            const stat1 = possibleStats[Math.floor(Math.random() * possibleStats.length)];
            const stat2Pool = possibleStats.filter((s) => s !== stat1);
            const stat2 = stat2Pool[Math.floor(Math.random() * stat2Pool.length)];
            const applyRelicStat = (stat, primary) => {
                const scale = (primary ? 0.8 : 0.5) * relicLevelScale;
                if (stat === 'critChance')
                    statBonuses.critChance = +((statBonuses.critChance || 0) + 0.012 * mult * scale).toFixed(3);
                else if (stat === 'critDamage')
                    statBonuses.critDamage = +((statBonuses.critDamage || 0) + 0.06 * mult * scale).toFixed(2);
                else if (stat === 'hp')
                    statBonuses.hp = (statBonuses.hp || 0) + Math.round(basePower * mult * 1.2 * scale);
                else if (stat === 'mp')
                    statBonuses.mp = (statBonuses.mp || 0) + Math.round(basePower * mult * 0.8 * scale);
                else if (stat === 'spellPower')
                    statBonuses.spellPower = (statBonuses.spellPower || 0) + Math.round(basePower * mult * 0.5 * scale);
                else
                    statBonuses[stat] = (statBonuses[stat] || 0) + Math.round(basePower * mult * 0.5 * scale);
            };
            applyRelicStat(stat1, true);
            if (stat2 !== stat1)
                applyRelicStat(stat2, false);
            break;
        }
    }
    // Archon's Focus — roll a per-drop max-stack ceiling within the rarity range
    // so two copies of the same tier can end up with different caps (e.g. one
    // legendary rolls 82, another rolls 94). Stored on the item so it's stable
    // across re-equips and save loads.
    let maxStacksRoll;
    if (isRelic && relicDef && relicDef.name === "Archon's Focus") {
        const rollInRange = (lo, hi) => lo + Math.floor(Math.random() * (hi - lo + 1));
        if (rarity === 'legendary')
            maxStacksRoll = rollInRange(75, 99);
        else if (rarity === 'epic')
            maxStacksRoll = rollInRange(50, 75);
        else
            maxStacksRoll = rollInRange(30, 50); // rare (relic floor)
    }
    // Special effects: relics use their paired effect, rare+ weapons get type-specific effects.
    // Per-NAME pools take precedence over per-TYPE pools — Runewhisper rolls
    // its own Runic Surge SP bonus instead of the generic Sword Vorpal.
    let specialEffect;
    if (isRelic && relicDef) {
        specialEffect = relicDef.effect;
    }
    else if ((slot === 'weapon1' || slot === 'weapon2') && baseWeaponName) {
        const effectPool = WEAPON_EFFECTS[name] || WEAPON_EFFECTS[baseWeaponName];
        if (effectPool) {
            const rarityIdx = RARITY_LIST.indexOf(rarity);
            if (rarityIdx >= 2) { // rare or higher
                const chosen = effectPool[Math.floor(Math.random() * effectPool.length)];
                if (rarity === 'legendary')
                    specialEffect = chosen.legendary;
                else if (rarity === 'epic')
                    specialEffect = chosen.epic;
                else
                    specialEffect = chosen.rare;
            }
        }
    }
    return {
        id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name,
        slot,
        rarity,
        classRestriction: null,
        weaponType,
        armorArchetype,
        armorSubtype,
        level: itemLevel,
        statBonuses,
        specialEffect,
        sellValue: Math.round((5 + itemLevel * 3) * RARITY_SELL_MULT[rarity] * (slot.startsWith('relic') ? 1.6 : slot === 'armor' ? 1.3 : 1.0)),
        spriteKey: getSpriteKey(slot, name, isRelic, rarity),
        levelRequirement: itemLevel,
        maxStacksRoll,
    };
}
/** Spell pool by hero class or heroKey — dropped from boss kills. */
export const BOSS_SPELL_POOL = {
    warrior: [
        { id: 'whirlwind', name: 'Whirlwind', description: 'Sweeping strike hits all nearby enemies',
            manaCost: 0, cooldown: 8000, currentCooldown: 0, damage: 60, healAmount: 0, effectType: 'damage', iconKey: 'spell_whirlwind', level: 1, maxLevel: 10 },
    ],
    tank: [
        { id: 'taunt', name: 'Taunt', description: 'Forces enemies to focus you',
            manaCost: 0, cooldown: 10000, currentCooldown: 0, damage: 20, healAmount: 0, effectType: 'damage', iconKey: 'spell_taunt', level: 1, maxLevel: 10 },
    ],
    wizard: [
        { id: 'fireball', name: 'Fireball', description: 'Hurl a ball of flame, dealing damage on impact and leaving the target burning.',
            manaCost: 5, cooldown: 4000, currentCooldown: 0, damage: 70, healAmount: 0, effectType: 'fireball', dotFraction: 0.30, effectDuration: 4000, iconKey: 'spell_fireball', level: 1, maxLevel: 10 },
    ],
    necromancer: [
        { id: 'death_coil', name: 'Death Coil', description: 'Shadow bolt that damages and heals Morveth for 30% of damage dealt.',
            manaCost: 18, cooldown: 24000, currentCooldown: 0, damage: 90, healAmount: 0, effectType: 'death_coil', dotFraction: 0.25, effectDuration: 5000, iconKey: 'spell_death_coil', level: 1, maxLevel: 10 },
    ],
    archer: [
        { id: 'barrage', name: 'Barrage', description: 'Fires a volley of arrows',
            manaCost: 15, cooldown: 5000, currentCooldown: 0, damage: 45, healAmount: 0, effectType: 'damage', iconKey: 'spell_barrage', level: 1, maxLevel: 10 },
    ],
    healer: [
        { id: 'mass_heal', name: 'Mass Heal', description: 'Heals all allied heroes',
            manaCost: 35, cooldown: 8000, currentCooldown: 0, damage: 0, healAmount: 80, effectType: 'heal', iconKey: 'spell_mass_heal', level: 1, maxLevel: 10 },
    ],
    amazon: [
        { id: 'leap_strike', name: 'Leap Strike', description: 'Launch into the air and crash down on the target — ignores most armor.',
            manaCost: 40, cooldown: 28000, currentCooldown: 0, damage: 120, healAmount: 0, effectType: 'jump_strike', iconKey: 'spell_leap_strike', level: 1, maxLevel: 10 },
    ],
    assassin: [
        { id: 'wraith_strike', name: 'Dirty Blades', description: 'Vanishes into shadow and strikes from behind, unseen',
            manaCost: 35, cooldown: 20000, currentCooldown: 0, damage: 80, healAmount: 0, effectType: 'shadow_step', iconKey: 'spell_poison_cloud', level: 1, maxLevel: 10 },
    ],
    dwarf: [
        { id: 'thunder_strike', name: 'Thunder Strike', description: 'Guaranteed critical strike',
            manaCost: 30, cooldown: 14000, currentCooldown: 0, damage: 160, healAmount: 0, effectType: 'thunder_strike', iconKey: 'spell_thunder_strike', level: 1, maxLevel: 10 },
    ],
    druid: [
        { id: 'thornlash', name: 'Thornlash', description: 'Whip of thorns — damage + bleed.',
            manaCost: 16, cooldown: 6000, currentCooldown: 0, damage: 55, healAmount: 0, effectType: 'dot', dotFraction: 0.30, effectDuration: 8000, iconKey: 'spell_thornlash', level: 1, maxLevel: 10 },
    ],
};
export function generateSpell(heroClass, heroKey) {
    const pool = BOSS_SPELL_POOL[heroKey ?? heroClass] ?? BOSS_SPELL_POOL[heroClass] ?? BOSS_SPELL_POOL['warrior'];
    return { ...pool[Math.floor(Math.random() * pool.length)] };
}
export const TROPHY_POOL = [
    // Anti-creature bonuses
    { name: 'Beastslayer Fang', effect: '+55% damage against beasts', iconKey: 'icon_skull', rarity: 'epic' },
    { name: 'Bone Collector', effect: '+55% damage against undead', iconKey: 'icon_chain', rarity: 'epic' },
    { name: 'Orcbane Tusk', effect: '+55% damage against orcs', iconKey: 'icon_necklace', rarity: 'epic' },
    { name: 'Demonbinder Seal', effect: '+55% damage against demons', iconKey: 'icon_bomb', rarity: 'epic' },
    { name: 'Dragonheart Shard', effect: '+40% damage against dragons, +25% Fire Resist', iconKey: 'icon_gems', rarity: 'legendary', statBonuses: { fireResist: 25 } },
    { name: 'Frostborn Essence', effect: '+20% Frost Resist for the equipped hero', iconKey: 'icon_mana_crystal', rarity: 'legendary', statBonuses: { frostResist: 20 } },
    { name: 'Shadewarden Sigil', effect: '+20% Shadow Resist for the equipped hero', iconKey: 'icon_chain', rarity: 'legendary', statBonuses: { shadowResist: 20 } },
    { name: 'Venomthorn Bark', effect: '+20% Nature Resist for the equipped hero', iconKey: 'icon_venomthorn', rarity: 'legendary', statBonuses: { natureResist: 20 } },
    // Defensive trophies
    { name: 'Ironhide Pelt', effect: 'All heroes take 20% less damage from melee attacks', iconKey: 'icon_armor_belt', rarity: 'epic' },
    { name: 'Arcane Ward Stone', effect: '+5% all resist to all heroes', iconKey: 'icon_goggles', rarity: 'epic' },
    { name: 'Lifeblood Vial', effect: '+45 HP regen per second for all heroes', iconKey: 'icon_potion_green', rarity: 'epic' },
    // Offensive trophies
    { name: 'Bloodfury Totem', effect: '+10% attack speed for all heroes', iconKey: 'icon_bloodfury_totem', rarity: 'legendary' },
    { name: 'Elixir of Giants', effect: '+25% base attack damage for the equipped hero', iconKey: 'icon_potion_purple', rarity: 'legendary' },
    { name: 'Soulreaper Charm', effect: 'Kills have 15% chance to restore 10% max HP', iconKey: 'icon_armor_gloves', rarity: 'epic' },
    { name: 'Goblin War Horn', effect: '+12% crit chance for the equipped hero', iconKey: 'icon_armor_helmet', rarity: 'legendary' },
    // Goblin Kingpin — Horde of Goblins event reward. Party-wide effect, NEVER rolls
    // from generic trophy drops; granted only by `grantHordeReward` after a horde clear.
    { name: 'Goblin Kingpin', effect: '+10% crit damage and +2 swap moves per wave for all heroes', iconKey: 'icon_goblin_kingpin', rarity: 'legendary' },
    // Economy / utility trophies
    { name: 'Treasure Magnet', effect: '+40% gold from all kills', iconKey: 'icon_gems', rarity: 'epic' },
    { name: 'Lucky Goblin Foot', effect: '+12% item drop chance', iconKey: 'icon_armor_boots', rarity: 'legendary' },
    { name: 'Mana Crystal', effect: '+50% mana regen for all heroes', iconKey: 'icon_mana_crystal', rarity: 'epic' },
];
/**
 * Generate a trophy item for the goblino shop.
 * Trophy items go in the 'reserved' slot, have no random stat bonuses,
 * and are always Epic or Legendary.
 */
/** Construct the Goblin Kingpin trophy directly — used by the Horde of
 *  Goblins reward flow. Bypasses the random pool because this is the only
 *  way to obtain it; `generateTrophy` filters it out of the random roll. */
export function generateGoblinKingpin(level) {
    const def = TROPHY_POOL.find((t) => t.name === 'Goblin Kingpin');
    return {
        id: `trophy_kingpin_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: def.name,
        slot: 'reserved',
        rarity: def.rarity,
        classRestriction: null,
        level,
        statBonuses: { ...(def.statBonuses ?? {}) },
        specialEffect: def.effect,
        sellValue: 100 + level * 10,
        spriteKey: def.iconKey,
        levelRequirement: 1,
    };
}
export function generateTrophy(level) {
    // Goblin Kingpin is event-only (Horde of Goblins reward) — strip from the
    // random goblino-shop drop pool so the only path to it is the horde event.
    const rollPool = TROPHY_POOL.filter((t) => t.name !== 'Goblin Kingpin');
    const def = rollPool[Math.floor(Math.random() * rollPool.length)];
    return {
        id: `trophy_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
        name: def.name,
        slot: 'reserved',
        rarity: def.rarity,
        classRestriction: null,
        level,
        statBonuses: { ...(def.statBonuses ?? {}) },
        specialEffect: def.effect,
        sellValue: def.rarity === 'legendary' ? 100 + level * 10 : 60 + level * 6,
        spriteKey: def.iconKey,
        levelRequirement: 1,
    };
}
/**
 * Return the level-scaled passive effect description for a relic.
 * For relics whose numeric values grow with item level (+4% per level),
 * this shows the actual effective value rather than the base text.
 * For non-relic items, or unknown names, returns the raw specialEffect string.
 *
 * `maxStacksRoll` is optional — Archon's Focus uses it to render the per-drop
 * rolled cap in its description so the player sees the actual number (e.g.
 * "Max: 82") rather than the rarity range.
 */
export function getRelicScaledEffect(name, level, maxStacksRoll) {
    const s = 1 + (level - 1) * 0.04;
    const p = (base) => `${Math.round(base * s * 100)}%`;
    const hp = (base) => `${(base * s).toFixed(1).replace(/\.0$/, '')}`;
    switch (name) {
        case 'Warblade Sigil': return `Auto attacks deal +${p(0.15)} bonus damage`;
        case 'Orb of Arcane Fury': return `Spell damage increased by ${p(0.12)}`;
        case 'Emblem of Precision': return `Critical strikes deal +${p(0.25)} extra damage`;
        case 'Idol of Swiftness': return `+${p(0.08)} attack speed`;
        case 'Talisman of the Fifth': return 'Every 5th attack deals double damage (stacks: reduces interval to every 4th, 3rd, 2nd)';
        case 'Charm of Twin Strikes': return `Attacks have ${p(0.10)} chance to strike twice`;
        case 'Totem of Bloodlust': return 'Kills grant +20% damage for 5s';
        case 'Aegis of Stoneskin': return `Reduce incoming damage by ${p(0.08)}`;
        case 'Ward of the Guardian': {
            // Shield amount scales as % of hero max HP (8% → 30% from lv 1 → lv 50).
            // 4 s internal cooldown — prevents permanent shield uptime at high attack-speed encounters.
            const shieldPct = Math.round(Math.min(0.30, 0.08 + (level - 1) * 0.005) * 100);
            return `${p(0.15)} chance on hit to shield for ${shieldPct}% of max HP (4 s ICD)`;
        }
        case 'Chalice of Renewal': {
            // Scales 0.5% → 2% of max HP per second (lv 1 → lv 50 cap).
            const regenPct = Math.min(2.0, Math.max(0.5, 0.5 + (level - 1) * 0.03));
            return `+${regenPct.toFixed(1)}% max HP regen per second in combat`;
        }
        case 'Bulwark Emblem': return `Block ${p(0.15)} of damage when above 50% HP`;
        case 'Sigil of Vampirism': return `On hit: ${p(0.10)} chance to heal for 5% max HP`;
        case 'Goldseer Idol': return `+${p(0.20)} gold from kills`;
        case 'Book of Enlightenment': return `+${p(0.15)} XP from all sources`;
        case 'Scavenger Charm': return `Enemies drop +${p(0.10)} more loot`;
        case 'Fortunekeeper Orb': return `+${p(0.10)} loot drop chance`;
        case 'Mindstone Talisman': return `Abilities cost ${p(0.10)} less resource`;
        case 'Chronoshard': return `Ability cooldowns reduced by ${p(0.08)}`;
        case 'Soulspark Sigil': return `Critical strikes restore ${hp(5)} resource`;
        case 'Manafount Charm': return `Gain ${hp(3)} mana on each auto-attack`;
        case 'Frostgrasp Totem': {
            // 10 % floor at level 1, +1 % per level past 10, capped at 50 % at lv 50.
            const slowPct = Math.min(50, Math.max(10, level));
            return `Nearby enemies move ${slowPct}% slower (10% base, +1% / lv past 10, max 50%)`;
        }
        case 'Slayer Emblem': return '+25% attack speed for 5s after killing an enemy';
        case 'Vanguard Ward': return `Start each wave with a ${hp(60)} HP shield`;
        case 'Crescendo Idol': return 'Gain 1% damage for each wave cleared (max 50 stacks)';
        case 'Vitality Idol': return 'Gain +0.2% max HP per kill (max 500 stacks → +100% HP, resets on death)';
        case "Archon's Focus":
            return maxStacksRoll
                ? `Gain +1% spell power per kill (max ${maxStacksRoll} stacks, resets on death).`
                : 'Gain +1% spell power per kill. Max stacks rolled per drop (rare 30-50, epic 50-75, legendary 75-99). Resets on death.';
        case "Spellbinder's Ring": return 'Spells have a 10% chance to cast twice';
        // Unique-tier — fixed text (no level scaling)
        case 'Resonant Prong': return 'Critical hits deal their damage a second time at 50% value.';
        case 'Bloodtally Knife': return '5% chance on kill to permanently gain +1% attack (stacks up to +100% per run).';
        case 'Timestop Pendant': return 'Once per 90s: prevents lethal damage on any hero and freezes all enemies for 4s.';
        case 'Storm Capacitor': return '25% chance on auto-attack to arc lightning to a random enemy for 80% attack damage.';
        case 'Overflow Crystal': return 'Doubles max Rage/Energy/Mana. Resource regeneration increased by 80%.';
        case "Forgemaster's Ingot": return 'All equipped weapons on this hero grant +40% of their stat bonuses.';
        case 'Spellfire Lens': return 'Spell damage multiplied by (1 + SP/200). At 200 SP this doubles all spell damage.';
        case 'Echo Catalyst': return "25% chance after casting a spell to echo it — deals 50% of hero's spell power as bonus arcane damage.";
        case 'Radiant Surge': return 'After the healer casts a heal, the target gains +80% damage for 3 seconds.';
        case 'Mana Flux Crystal': return 'When mana/energy is full, the next ability deals +80% damage and drains all resource.';
        default: return '';
    }
}
/**
 * Return the combined relic passive % bonuses for a hero's equipped relics.
 * Mirrors the same formula used in BattleScene.getRelicBonuses() so the
 * stats panel can display accurate values without touching BattleScene.
 */
export function getRelicPassiveBonuses(hero) {
    const b = { attackPct: 0, spellPct: 0, speedPct: 0, critDmgBonus: 0, dmgReduction: 0, hpRegen: 0 };
    for (const slot of ['relic1', 'relic2', 'relic3', 'relic4']) {
        const item = hero.equipment[slot];
        if (!item)
            continue;
        const s = 1 + (item.level - 1) * 0.04;
        switch (item.name) {
            case 'Warblade Sigil':
                b.attackPct += 0.15 * s;
                break;
            case 'Orb of Arcane Fury':
                b.spellPct += 0.12 * s;
                break;
            case 'Idol of Swiftness':
                b.speedPct += 0.08 * s;
                break;
            case 'Emblem of Precision':
                b.critDmgBonus += 0.25 * s;
                break;
            case 'Aegis of Stoneskin':
                b.dmgReduction += 0.08 * s;
                break;
            case 'Chalice of Renewal': {
                // % of max HP per second; multiple copies take MAX, not stack.
                // Lv 1 = 0.5%/s, lv 50 = 2%/s (cap).
                const lvlPct = Math.min(0.02, Math.max(0.005, 0.005 + (item.level - 1) * 0.0003));
                if (lvlPct > b.hpRegen)
                    b.hpRegen = lvlPct;
                break;
            }
        }
    }
    return b;
}
/**
 * Generate equipment of a specific type (weapon, armor, or relic).
 * Forces the slot category instead of using random weighted selection.
 * Handles unique rarity for relics directly from UNIQUE_RELIC_POOL.
 */
export function generateEquipmentForType(itemType, itemLevel, lootTier, enemyTier = 'elite', forceRarity) {
    // Unique rarity is relic-only — generate directly from the unique pool
    if (forceRarity === 'unique') {
        const slot = 'relic1';
        const uniqueDef = UNIQUE_RELIC_POOL[Math.floor(Math.random() * UNIQUE_RELIC_POOL.length)];
        return {
            id: `item_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
            name: uniqueDef.name,
            slot,
            rarity: 'unique',
            classRestriction: null,
            level: itemLevel,
            statBonuses: {},
            specialEffect: uniqueDef.effect,
            sellValue: RARITY_SELL_MULT['unique'],
            spriteKey: getSpriteKey(slot, uniqueDef.name, true),
            levelRequirement: itemLevel,
        };
    }
    // Keep re-rolling until we get the desired slot category (max 30 attempts).
    // When forceRarity is set, pass it via exactRarity so every derived field
    // (name prefix, stat mult, weapon effect) matches the final rarity — prevents
    // the "common item with legendary stats" mismatch.
    for (let attempt = 0; attempt < 30; attempt++) {
        const equip = generateEquipment(itemLevel, lootTier, enemyTier, undefined, 1.0, 0, forceRarity);
        const slotMatch = (itemType === 'weapon' && (equip.slot === 'weapon1' || equip.slot === 'weapon2')) ||
            (itemType === 'armor' && equip.slot === 'armor') ||
            (itemType === 'relic' && equip.slot.startsWith('relic'));
        if (slotMatch)
            return equip;
    }
    // Fallback: generate once more and coerce slot (rarity still exact via exactRarity).
    const equip = generateEquipment(itemLevel, lootTier, enemyTier, undefined, 1.0, 0, forceRarity);
    if (itemType === 'armor')
        equip.slot = 'armor';
    else if (itemType === 'weapon')
        equip.slot = 'weapon1';
    else
        equip.slot = 'relic1';
    return equip;
}
/**
 * Roll loot from killing an enemy.
 */
/**
 * lootDropMult — combined multiplicative bonus from all loot-chance sources
 * (Luck tome, Lootfi passive, Scavenger/Fortunekeeper relics, Lucky Goblin Foot trophy).
 * A value of 1.0 means no bonus. 1.1 means +10% of base chance.
 * Applied as: adjusted_chance = base_chance × lootDropMult
 * so a base 10% chance with 1.1× mult becomes 11%, never 20%.
 */
export function rollLoot(enemy, zoneLevel, killerClass, killerHeroKey, lootDropMult = 1.0, rarityUpMult = 1.0, rareLootUpgrade = 0) {
    const drop = {
        gold: Math.floor(enemy.goldReward * 0.5), // gold from kills halved; other sources (quests, events) unaffected
        xp: enemy.xpReward,
        items: [],
        loot: 0,
        spells: [],
    };
    // Roll for item drop — all loot-chance bonuses baked into lootDropMult (multiplicative on base)
    if (Math.random() < enemy.lootChance * LOOT_DROP_GLOBAL_MULT * 1.5 * lootDropMult) {
        drop.items.push(generateEquipment(zoneLevel, enemy.lootTier, enemy.tier, undefined, rarityUpMult, rareLootUpgrade));
    }
    // Boss loot guarantee: always at least 1 item meeting minimum rarity
    if (enemy.tier === 'boss') {
        const minRarity = zoneLevel >= 10 ? 'epic' : 'rare';
        const rarityOrder = ['common', 'uncommon', 'rare', 'epic', 'legendary'];
        if (drop.items.length === 0) {
            drop.items.push(generateEquipment(zoneLevel, enemy.lootTier, enemy.tier, minRarity, rarityUpMult, rareLootUpgrade));
        }
        else if (rarityOrder.indexOf(drop.items[0].rarity) < rarityOrder.indexOf(minRarity)) {
            drop.items[0] = generateEquipment(zoneLevel, enemy.lootTier, enemy.tier, minRarity, rarityUpMult, rareLootUpgrade);
        }
    }
    // Tokens: bosses always drop some; elites have a low chance
    if (enemy.tier === 'boss') {
        drop.loot = 1 + Math.floor(zoneLevel / 10);
    }
    else if (enemy.tier === 'elite') {
        drop.loot = Math.random() < 0.125 ? 1 : 0;
    }
    // Boss spell drop: 20% chance when killer class is known
    if (enemy.tier === 'boss' && killerClass && Math.random() < 0.2) {
        drop.spells.push(generateSpell(killerClass, killerHeroKey));
    }
    return drop;
}
//# sourceMappingURL=loot.js.map